<template>
  <div>
    <el-tabs v-model="menu">
      <el-tab-pane name="agreement">
        <template #label><span><i class="fa fa-file-signature text-muted"></i> Sharer Agreement</span></template>

        <el-row>
          <el-col :xs="24" :sm="12" :md="6">
            Agreement Start <strong>{{formatDate(details.started)}}</strong>
          </el-col>

          <el-col :xs="24" :sm="12" :md="6">
            Agreement Expiry <strong>{{formatDate(details.expiry)}}</strong>
          </el-col>

          <el-col :xs="24" :sm="12" :md="6">
            Agreement Term <strong v-if="term !== null">{{term}} months</strong><strong v-else>—</strong>
          </el-col>

          <el-col :xs="24" :sm="12" :md="6">
            Expires <strong>{{expiry !== null ? expiry : '—'}}</strong>
          </el-col>
        </el-row>

        <!-- XOL-462: strict_position_out_of_range / strict_term_exceeded banner.
             Deliberately OUTSIDE the <el-form> below — an alert inside an inline
             form reflows the input rows when it appears or disappears
             (ProjectTab.vue:12-17). -->
        <sharer-billing-issues :issues="billingIssues" />

        <p>
            <h3>Site Financials</h3>
            <!-- XOL-516 (Carlos, 2026-09-15): role set widened to add
                 `management` and `admin`. This DELIBERATELY REVERSES XOL-371
                 ruling A4-1 (2026-07-21), which set this to finance|project as
                 part of "revert over-relaxed Tower-Co gates". See the XOL-516
                 section in docs/plans/2026-07-21-xol-371-gating-review-by-url.md
                 for why that ruling was revisited. Do not "restore" it to the
                 July value without reading that. -->
            <el-button v-if="isManager('finance|project|management|admin') && !readOnly" type="text" aria-label="Edit sharer agreement" @click="editState()"><i :class="editing ? 'fa fa-check-circle text-success' : 'fa fa-edit'"></i></el-button>
            <el-button v-if="this.editing" type="text" @click="editing = false"><i class="fa fa-times-circle text-danger"></i></el-button>
        </p>

        <!-- XOL-467: any-to-all auto-fill notice. Outside <el-form> for the same
             reason as the billing-issues banner above — appearing/disappearing
             must never reflow the inline input rows. No Handover input on this
             tab, so the notice only ever concerns Agreement Start / Billing
             Start. -->
        <el-alert v-if="editing && autoFilled" type="info" show-icon :closable="false"
          description="Billing Start was blank, so it has been filled in from the date you just set. Adjust it if it differs." />

        <el-row>
          <el-col :span="24">
            <el-form inline label-position="top" ref='financeForm' :model='details'>

              <el-row :gutter="12">
                <el-col :span="24">
                  <select-sharer-status v-model="details.status" :disabled="!editing" />

                  <span v-if="details.started || editing" class="handover-field">
                    <date-input label="Agreement Start" v-model="details.started" :disabled="!editing"
                      ref="dateField_started"
                      :class="[{'licensee-date--editing': editing}, {'licensee-date--handover-gutter': showHandoverAction}]"
                      :error="fieldErrors.started" @change="onDateChange('started', $event)" />
                    <!-- XOL-479: this tab has no Handover input (Carlos, 2026-09-01 —
                         do not add one here). When the block/backstop names this field
                         as the one to fix, send the user to the only screen that CAN
                         fix it, without losing this in-progress edit (see
                         openHandoverOnProjectTab). Rendered only for the exact
                         handover-required message — never for the other `started`
                         messages, which this tab can resolve locally. -->
                    <el-button v-if="showHandoverAction" type="text" size="small"
                      class="handover-field__action" @click="openHandoverOnProjectTab">
                      Open Project tab to set Handover <i class="fa fa-arrow-right" aria-hidden="true"></i>
                    </el-button>
                  </span>
                  <date-input v-if="details.status !== 'Terminated' && (details.startBilling || editing)" label="Billing Start" v-model="details.startBilling" :disabled="!editing"
                    ref="dateField_startBilling" :class="{'licensee-date--editing': editing}"
                    :error="fieldErrors.startBilling" @change="onDateChange('startBilling', $event)" />
                  <date-input v-if="details.status == 'Terminated' && (details.terminated || editing)" label="Terminated" v-model="details.terminated" :disabled="!editing"
                    :class="{'licensee-date--editing': editing}" @change="clearDateErrors" />
                  <date-input v-if="details.expiry || editing" label="Expiry" v-model="details.expiry" :disabled="!editing"
                    ref="dateField_expiry" :class="{'licensee-date--editing': editing}"
                    :error="fieldErrors.expiry" @change="onDateChange('expiry', $event)" />

                </el-col>
              </el-row>

              <!-- Sharer Rate on its own row + Duplicate & modify (XOL-241) -->
              <!-- XOL-355: hidden when the rate is manually overridden — the tiered
                   sharer rate is irrelevant while a flat override is in effect. -->
              <el-row v-if="!details.overrideEnabled" :gutter="12">
                <el-col :span="24">
                  <sharer-rate-override v-model="details.rate_id" :sharer-id="sharer.organisation_id" :disabled="!editing" @input="onRateChanged" />
                </el-col>
              </el-row>

              <!-- XOL-306: in-form version select (edit mode, version-managers only) -->
              <el-row v-if="editing && details.rate_id && canSwitchVersion && !details.overrideEnabled" :gutter="12">
                <el-col :sm="24" :md="16">
                  <el-form-item label="Rate version">
                    <!-- XOL-497 fold-in (Carlos, 2026-09-13): a licensee is always
                         pinned to a PUBLISHED version, so this control must not
                         offer or imply an unpinned state.

                         `clearable` removed. Clearing set selectedVersionId to
                         null, and on a rate change commitFinance() sends that
                         null explicitly — which API XOL-507 now rejects with a
                         422. The SPA was offering an action the API refuses.
                         Every option in the list is a rate_versions row, which
                         only publish() can create, so the options themselves
                         were never the problem.

                         The placeholder said "Latest (unversioned)", naming the
                         state we do not allow. It shows only for a legacy row
                         with no pin; "Latest (published)" names what such a row
                         resolves to and what a save will pin it to. -->
                    <select-rate-versions
                      :versions="rateVersions"
                      :value="selectedVersionId"
                      :loading="loadingVersions"
                      :disabled="!canSwitchVersion"
                      placeholder="Latest (published)"
                      @input="onSelectVersion" />
                  </el-form-item>
                </el-col>
              </el-row>

              <!-- XOL-259 Phase 2 / XOL-306: read-only version summary (view mode) -->
              <!-- XOL-355: hidden when the rate is manually overridden. -->
              <el-row v-if="!details.overrideEnabled" :gutter="12">
                <el-col :span="24">
                  <el-divider content-position="left">Rate version</el-divider>
                  <sharer-rate-version v-show="!editing" ref="sharerRateVersion" :id="sharer.id" :rate-id="details.rate_id" />
                </el-col>
              </el-row>

              <!-- Transient note after a rate or version change — XOL-306 -->
              <el-alert
                v-if="resumingFee"
                type="info"
                :closable="false"
                show-icon
                title=""
                description="Fees are recalculating in the background — this may take a moment." />

              <el-alert v-if="details && editing" type="warning" title="Sharer specific!" description="Only input values PER sharer. For site wide CAPEX and OPEX see Finance Summary." show-icon  :closable="false" />
                <!-- XOL-319: vertical gap between the Rate version table and the Calculated (auto) rate -->
                <el-row :gutter="12" style="margin-top: 16px;">
                    <el-col :span="24">

                      <!-- XOL-268 Phase B: Current Fees + override controls -->
                      <currency-input
                        v-if="details.overrideEnabled"
                        label="Current Fees"
                        v-model="details.currentRate"
                        :disabled="!editing || !canOverride"
                        placeholder="Enter manual fee" />

                      <!-- Override checkbox — only visible when editing -->
                      <el-tooltip
                        v-if="editing"
                        content="Only finance, project, management or admin managers can override the rate."
                        placement="bottom"
                        :disabled="canOverride">
                        <check-box
                          v-if="editing"
                          class="override-lock-label"
                          :label="'Override/Lock\nRate'"
                          v-model="details.overrideEnabled"
                          :disabled="!canOverride" />
                      </el-tooltip>

                      <!-- Non-manager helper text (guaranteed visible fallback) -->
                      <p v-if="editing && !canOverride" class="text-muted" style="font-size:12px;margin:0 0 8px;">
                        Only finance, project, management or admin managers can override the rate.
                      </p>

                      <!-- Calculated (auto) rate display — XOL-268 -->
                      <el-form-item v-if="!details.overrideEnabled" label="Calculated (auto) rate">
                        <span v-if="details.calculatedRate !== null && details.calculatedRate !== undefined">{{calcRateDisplay}}</span>
                        <span v-else>
                          <span>—</span>
                          <span class="text-muted" style="font-size:12px;margin-left:6px;">No calculated rate yet (no rate set, no handover, or past the last tier).</span>
                        </span>
                      </el-form-item>

                      <!-- Transient note after clearing override — XOL-268 -->
                      <el-alert
                        v-if="resumingAuto"
                        type="info"
                        :closable="false"
                        show-icon
                        title=""
                        description="Resuming the calculated rate — this may take a moment to update." />

                      <float-input
                        v-if="details.discount || editing"
                        v-model="details.discount"
                        label="Discount" :prefix="details.discountPercentage ? currency : ''"
                        :suffix="details.discountPercentage ? '' : '%'"
                        :disabled="!editing" />

                        <check-box
                          v-if="editing || details.discount"
                          label="Disc. Type"
                          v-model="details.discountPercentage"
                          :disabled="!editing">
                          {{!details.discountPercentage ? '%' : currency}}
                        </check-box>

                        <float-input
                            v-if="details.interestRate || editing"
                            v-model="details.interestRate"
                            label="Interest Rate"
                            suffix="%"
                            :disabled="!editing" />

                      <float-input v-if="details.capex || editing" label="Sharer Specific CAPEX" v-model="details.capex" :decimals="2" :disabled="!editing" />
                      <float-input v-if="details.opex || editing" label="Sharer Specific OPEX" v-model="details.opex" :decimals="2" :disabled="!editing"  />
                </el-col>

              </el-row>

              <div v-if="details.updated_at">
                <small class="text-muted pull-right">Last update: <b>{{formatFriendlyDate(details.updated_at)}}</b><span v-if="details.updated_by"> by {{details.updated_by}}</span></small>
              </div>

          </el-form>

        </el-col>
      </el-row>

      <el-row v-if="canAccess('finance') && sharer.site_id && sharer.id">
        <el-col :span="24">
          <files-panel title="License Agreement" type="site" :id="sharer.site_id" process="sharer" :process-id="sharer.id" hide-carousel />
        </el-col>
      </el-row>

   </el-tab-pane>


   <el-tab-pane name="sharerInvoice" lazy>
     <template #label><span><i class="fa fa-money-check text-muted"></i> Sharer Invoice</span></template>

     <site-invoice :site-data="siteData" :sharer-id="sharer.organisation_id" :org="sharer" />
   </el-tab-pane>

   <el-tab-pane name="sharerVO" lazy>
     <template #label><span><i class="fa fa-screwdriver text-muted"></i> Sharer V.O.</span></template>

     <site-invoice :site-data="siteData" :sharer-id="sharer.organisation_id" :org="sharer" is-vo />
   </el-tab-pane>


   <el-tab-pane name="billableEquip" lazy>
     <template #label><span><i class="fa fa-tools text-muted"></i> Billable Equip.</span></template>
     <billable-equip-table :site-id="details.site_id" :site-data="siteData" :owner-id="sharer.organisation_id" />
   </el-tab-pane>

   <el-tab-pane name="feeSchedule" lazy>
     <template #label><span><i class="fa fa-calendar-alt text-muted"></i> Fee Schedule</span></template>
     <sharer-fee-schedule :id="sharer.id" :started="details.started" />
   </el-tab-pane>

   <el-tab-pane name="feeTimeline" lazy>
     <template #label><span><i class="fa fa-stream text-muted"></i> Fee Timeline</span></template>
     <sharer-fee-timeline :id="sharer.id" :started="details.started" />
   </el-tab-pane>

 </el-tabs>
  </div>
</template>


<script>

import axios from '@/lib/axiosClient'
import PostMixins from '@/mixins/PostMixins'
import NumberMixins from '@/mixins/NumberMixins'
import DateMixins from '@/mixins/DateMixins'
import DateChainMixin from '@/mixins/DateChainMixin'
import {dateChainErrors, autoFillDates} from '@/lib/licenseeDates'
import {toAppTime, appNow} from '@/lib/appTime'
import {pushDrawer} from '@/lib/layerNav'


import {settings} from '@/components/Data'

import SelectSharerStatus from '@/components/atoms/selects/SelectSharerStatus'
import SelectRateVersions from '@/components/atoms/selects/SelectRateVersions'
import SharerRateOverride from '@/components/finance/rates/SharerRateOverride'

import BillableEquipTable from '@/components/assets/subcat/BillableEquipTable'

import SiteInvoice from '@/components/sites/finance/SiteInvoice'
import SharerFeeSchedule from './SharerFeeSchedule'
import SharerFeeTimeline from './SharerFeeTimeline'
import SharerRateVersion from './SharerRateVersion'
import SharerBillingIssues from './SharerBillingIssues'

// XOL-479: single source of truth for the handover-required copy — used by
// both the client-side block (dateChainCopy) and the 422 backstop
// (onSaveErrors), and compared against by `showHandoverAction` to decide
// whether the "open Project tab" affordance renders. Do NOT add a Handover
// input to this tab (Carlos, 2026-09-01) — the fix lives on the Project tab;
// this only gets the user there without losing their edit.
var HANDOVER_REQUIRED_MESSAGE = 'This sharer has no Handover date — set it on the Project tab before saving a date change.'

// XOL-467 (any-to-all widening): Agreement Start <-> Billing Start only —
// this tab has no Handover input, so Handover is neither source nor target
// here (Carlos, 2026-09-01). See licenseeDates.js's autoFillDates() doc.
var DATE_CHAIN_FIELDS = ['started', 'startBilling']

export default {
  mixins: [DateMixins, NumberMixins, PostMixins, DateChainMixin],

  props: {
    sharer: {
      type: Object,
      required: true
    },

    editMode: {
      type: Boolean,
      default: false
    },

    siteData: {
      type: Object
    },

    readOnly: {
      type: Boolean,
      default: false
    }
  },

  components: {
    BillableEquipTable,
    SelectSharerStatus,
    SelectRateVersions,
    SiteInvoice,
    SharerRateOverride,
    SharerFeeSchedule,
    SharerFeeTimeline,
    SharerRateVersion,
    SharerBillingIssues,
  },

  computed: {
    // Both are rendered unconditionally in the header, and `expiry` is
    // genuinely optional (line 42 gates its own input on `details.expiry ||
    // editing`). toAppTime returns null where the old moment(null) returned an
    // *invalid* moment — which absorbed .diff()/.fromNow() as NaN/'Invalid
    // date' — so these must guard or the whole tab throws.
    term: function () {
      var oExpiry = toAppTime(this.details.expiry)
      var oStarted = toAppTime(this.details.started)
      return (oExpiry === null || oStarted === null) ? null : oExpiry.diff(oStarted, 'months')
    },

    expiry: function () {
      var oExpiry = toAppTime(this.details.expiry)
      return (oExpiry === null) ? null : oExpiry.from(appNow())
    },

    // XOL-371 review 2026-07-21 (A4-2) set this to finance|project, matching the
    // edit-toggle gate. XOL-516 (Carlos, 2026-09-15) widens all three of this
    // component's gates together to add management|admin — kept identical on
    // purpose, because three sibling gates with two role sets is precisely how
    // the mismatch XOL-516 exists to fix came about.
    canOverride: function () {
      return this.isManager('finance|project|management|admin') && !this.readOnly
    },

    // XOL-259 Phase 2 / XOL-371 A4-3. Widened by XOL-516 with A4-1 and A4-2 —
    // same role set as canOverride and the edit toggle.
    //
    // This is the gate XOL-516 was filed about: the Min alert on the rate form
    // tells the reader to re-pin, and that form is gated
    // isManager('finance|management|admin'). With this set, every reader of
    // that alert can now reach the control it names.
    canSwitchVersion: function () {
      return this.isManager('finance|project|management|admin') && !this.readOnly
    },

    // XOL-268: formatted calculated rate, dash when null/undefined
    calcRateDisplay: function () {
      var v = this.details.calculatedRate
      if (v === null || v === undefined) {
        return '—'
      }
      return this.formatMoney(v, 2)
    },

    // XOL-479: gates the "Open Project tab to set Handover" affordance —
    // exact-match against the shared constant so it never lights up for a
    // DIFFERENT message that happens to also land on `started`
    // (started_required).
    showHandoverAction: function () {
      return this.fieldErrors.started === HANDOVER_REQUIRED_MESSAGE
    }
  },

  methods: {
    // XOL-462: modelled on ProjectTab.loadBillingMode() — a silent side-read.
    // Never invent a clean state from a failed fetch: leave billingIssues at
    // whatever it already was (data() seeds []).
    loadBillingIssues: function () {
      if (!this.details.id) return
      var self = this
      this.fetchData(
        'licensees/' + this.details.id + '/fee-schedule',
        function (data) {
          var meta = data && data.meta
          self.billingIssues = (meta && Array.isArray(meta.issues)) ? meta.issues : []
        },
        'billing issues',
        function () { /* leave empty — never invent a clean state from a failed fetch */ }
      )
    },

    // XOL-268: derive overrideEnabled — prefer the explicit field, fall back to rateLocked
    deriveOverride: function (obj) {
      if (obj && obj.overrideEnabled !== undefined && obj.overrideEnabled !== null) {
        return !!obj.overrideEnabled
      }
      return !!(obj && obj.rateLocked)
    },

    // XOL-373: every licensee write on this screen must re-read the audit stamp.
    //
    // The write responses differ in shape — the override calls and the re-pin
    // return `decorateSharer($lic->fresh())`, while `update()`'s default branch
    // returns the raw model (`SharerController.php:840`) — but ALL of them carry
    // `updated_at`/`updated_by`, which is all this needs.
    //
    // Only two of the four handlers folded them, so setting or clearing an
    // override left the "Last update … by X" line (rendered at the FOOT of the
    // form, and gated only on `updated_at`, so it shows whether or not you are
    // editing) crediting the PREVIOUS editor with the previous date.
    //
    // The fold itself lives in PostMixins so this screen and the Project/Utility
    // tabs share one definition — the gap arose from those lines being inlined
    // in some handlers and absent from others.
    applyStamp: function (data) {
      this.applyAuditStamp(this.details, data)
    },

    // XOL-268: fold response from PUT /rate-override into local state + reset baselines
    applyOverrideResponse: function (data) {
      if (!data) return
      if (data.currentRate !== undefined) {
        this.details.currentRate = data.currentRate
      }
      if (data.calculatedRate !== undefined) {
        this.details.calculatedRate = data.calculatedRate
      }
      var derived = this.deriveOverride(data)
      this.details.overrideEnabled = derived
      this.originalRate = data.currentRate
      this.originalOverride = derived
      // XOL-373: an override IS an edit — attribute it.
      this.applyStamp(data)
    },

    // XOL-268: PUT /licensees/{id}/rate-override
    saveRateOverride: function () {
      var self = this

      // Guard: must have a valid positive rate when override is enabled
      var rate = Number(this.details.currentRate)
      if (!this.details.currentRate || isNaN(rate) || rate <= 0) {
        this.$message({
          type: 'warning',
          title: 'Missing fee',
          message: 'Enter a Current Fee amount before overriding the calculated rate.'
        })
        this.editing = true
        return false
      }

      var path = '/licensees/' + this.details.id + '/rate-override'
      var url = this.getEndpoint(path)

      this.send(
        self,
        url,
        { currentRate: rate },
        'Rate override',
        function (data) {
          self.applyOverrideResponse(data)
        },
        null,
        axios.put,
        true,
        'Save'
      )
      return true
    },

    // XOL-268: DELETE /licensees/{id}/rate-override
    clearRateOverride: function () {
      var self = this

      var path = '/licensees/' + this.details.id + '/rate-override'
      var url = this.getEndpoint(path)

      this.send(
        self,
        url,
        null,
        'Rate override',
        function (data) {
          self.details.overrideEnabled = false
          self.originalOverride = false
          self.resumingAuto = true
          // Do NOT copy stale data.currentRate — let calculatedRate govern display
          if (data && data.calculatedRate !== undefined) {
            self.details.calculatedRate = data.calculatedRate
          }
          // XOL-373: clearing an override is an edit too. This path has its own
          // handler rather than applyOverrideResponse (which would copy the
          // stale currentRate the comment above refuses), so the stamp is
          // folded here explicitly — it is NOT reached via that method.
          self.applyStamp(data)
        },
        null,
        axios.delete,
        true,
        'Save'
      )
    },

    // XOL-306: load published versions for a rate; autoLatest=true pins selectedVersionId
    // to the current version automatically (on rate change), autoLatest=false seeds
    // the existing pin from originalVersionId (on edit-open).
    loadVersionsForRate: function (rateId, autoLatest) {
      var self = this
      // Only version-managers manage the pin; the /rates/{id}/versions endpoint is
      // gated finance|admin|management, so skip the fetch for finance|project-only
      // editors (avoids a 403 + spurious error toast). Their save omits
      // rate_version_id (see commitFinance) so the backend auto-pins on rate change.
      if (!this.canSwitchVersion) {
        this.rateVersions = []
        this.selectedVersionId = this.originalVersionId
        return
      }
      if (!rateId) {
        this.rateVersions = []
        this.selectedVersionId = null
        return
      }
      this.loadingVersions = true
      this.fetchData(
        'rates/' + rateId + '/versions',
        function (data) {
          self.rateVersions = Array.isArray(data) ? data : []
          if (autoLatest) {
            var current = self.rateVersions.find(function (v) { return v.is_current })
            self.selectedVersionId = current ? current.id : null
          } else {
            self.selectedVersionId = self.originalVersionId
          }
          self.loadingVersions = false
        },
        'rate versions',
        function (_err) {
          self.rateVersions = []
          self.selectedVersionId = autoLatest ? null : self.originalVersionId
          self.loadingVersions = false
        }
      )
    },

    // XOL-306: called by @input on sharer-rate-override; auto-defaults version to latest
    onRateChanged: function (rateId) {
      this.loadVersionsForRate(rateId, true)
    },

    // XOL-306: called by @input on select-rate-versions in edit mode
    onSelectVersion: function (id) {
      this.selectedVersionId = (id === undefined || id === null) ? null : id
    },

    // XOL-268 Phase B: extracted write sequence — receives already-computed booleans
    commitFinance: function (overrideChanged, rateChanged, rateIdChanged, versionPinNeeded, clearingRate) {
      if (this.details.overrideEnabled && (overrideChanged || rateChanged)) {
        if (this.saveRateOverride() === false) return  // empty-fee guard: keep editing, no bulk save
      } else if (!this.details.overrideEnabled && overrideChanged) {
        this.clearRateOverride()
      }
      var self = this
      var payload = this.clone(this.details)
      delete payload.currentRate
      delete payload.rateLocked
      // XOL-416: this payload is a blocklist off the full decorateSharer body, so it
      // echoed the sharer's billing slot back like the sharer tabs did. The slot is
      // not editable on this screen, so it is not sent.
      //
      // This is a real guard, not tidiness: it is what stops a RATE edit from moving
      // a sharer's BILLING POSITION as a side effect. Deleting the line would let any
      // stale `sharerNo` in `details` ride along on a save that has nothing to do
      // with it.
      //
      // (XOL-431 removed the neighbouring `delete payload.billing_no`. That column no
      // longer exists, so it stripped a key that was never in the body.)
      delete payload.sharerNo
      // XOL-306/XOL-312: the bulk PUT auto-pins ONLY when rate_id changes — send the
      // version-manager's chosen pin on a rate change so it's atomic + honoured.
      // A same-rate version switch is ignored by the bulk PUT, so it's persisted
      // via the dedicated re-pin endpoint in the success callback.
      // (XOL-516: the `canSwitchVersion &&` guards below are now unreachable-false
      // — the edit toggle and canSwitchVersion are the same predicate, so anyone
      // reaching commitFinance has it. Kept as defence, not as a live branch.)
      //
      // XOL-497: the id is sent ONLY when we actually have one. "null = unpin"
      // is no longer a supported operation — API XOL-507 refuses an explicit
      // null with a 422 on a rate change, and every licensee is pinned.
      // selectedVersionId can still be null here without the user asking for
      // it: the versions GET failing sets it null, and a legacy licensee with
      // no pin starts null. Sending that would 422 a save the user cannot fix
      // from this form. Omitting instead lets the API auto-pin is_current,
      // which is the intended outcome in both cases.
      delete payload.rate_version_id
      if (this.canSwitchVersion && rateIdChanged && this.selectedVersionId) {
        payload.rate_version_id = this.selectedVersionId
      }
      // XOL-514 (paired with API XOL-513): removing a licensee's rate stops its
      // billing, and the API used to accept that silently with a 200. It now
      // refuses a rate-clear on a licensee that HAS a rate unless the payload
      // says the removal was intended.
      //
      // Sent on every clear, including the overridden case. The flag answers
      // "did the user mean to remove the rate", which is true either way; it is
      // only the CONSEQUENCE that differs by override state, and that
      // difference lives in the confirm copy, not here.
      //
      // Normalising rate_id to null is not cosmetic: Element's clearable
      // el-select emits '', so without this the body carries `rate_id: ""` and
      // the clear only works because Laravel's ConvertEmptyStringsToNull
      // rewrites it upstream of validation. Sending what we mean removes the
      // dependency on that middleware staying in the stack.
      if (clearingRate) {
        payload.rate_id = null
        payload.unassign_rate = true
      }
      // XOL-467: no Handover input on this tab — never echo it back.
      // Sending it unconditionally would re-arm LicenseeRequest's row-level
      // chain invariant on every unrelated save (a pure rate change), which
      // is exactly the lockout the dirty-gate exists to remove for the ~72
      // Active licensees that already violate it. `started` / `startBilling`
      // / `expiry` are dirty-gated too — sent only when actually edited.
      delete payload.handover
      if (this.details.started === this.originalStarted) delete payload.started
      if (this.details.startBilling === this.originalStartBilling) delete payload.startBilling
      if (this.details.expiry === this.originalExpiry) delete payload.expiry
      this.saveForm(this, '/licensees', this.details.id, payload, 'Sharer Financial Data', function (resp) {
        if (rateIdChanged) {
          self.originalRateId = self.details.rate_id
        }
        self.originalStarted = self.details.started
        self.originalStartBilling = self.details.startBilling
        self.originalExpiry = self.details.expiry
        self.autoFilled = false
        // Same-rate version change → the bulk PUT ignores rate_version_id when the
        // rate is unchanged (XOL-312), so persist it via the dedicated re-pin
        // endpoint (XOL-259). (Same-rate unpin to null isn't persistable — the
        // endpoint requires an id — so that edge is skipped.)
        if (self.canSwitchVersion && !rateIdChanged && versionPinNeeded && self.selectedVersionId !== null) {
          self.repinVersion(self.selectedVersionId)
        } else {
          self.originalVersionId = self.selectedVersionId
          if (resp) self.onVersionRepinned(resp)
          // Reload the read-only summary after a rate change (its pin/rate moved);
          // a no-op save needs no refresh.
          if (rateIdChanged && self.$refs.sharerRateVersion) self.$refs.sharerRateVersion.load()
        }
        // Transient fee-recalc note when rate or version changed (async recompute)
        if (rateIdChanged || versionPinNeeded) {
          self.resumingFee = true
          // XOL-462: a rate or version change can create or clear a strict
          // violation — re-read the issues banner.
          self.loadBillingIssues()
        }
      }, function (errors) {
        // XOL-479: 422 backstop — see onSaveErrors().
        self.onSaveErrors(errors)
      })
      this.editing = false
    },

    // XOL-306: persist a SAME-RATE version switch via the dedicated re-pin endpoint
    // (the bulk PUT only auto-pins on a rate change — XOL-312). Mirrors the former
    // SharerRateVersion.doSwitch; folds the decorated licensee + refreshes the summary.
    repinVersion: function (versionId) {
      var self = this
      this.send(
        this,
        this.getEndpoint('/licensees/' + this.details.id + '/rate-version'),
        { rate_version_id: versionId },
        'Rate version',
        function (licensee) {
          self.originalVersionId = versionId
          if (licensee) self.onVersionRepinned(licensee)
          if (self.$refs.sharerRateVersion) self.$refs.sharerRateVersion.load()
        },
        function (_err) {
          // error toast handled by send; the read-only summary reflects server truth
        },
        axios.put,
        true,
        'Save',
        null,
        true
      )
    },

    // XOL-268 Phase B: compose the $confirm body from the applicable change bullets
    buildImpactMessage: function (overrideOn, overrideOff, rateChange, versionChange, clearingRate) {
      var items = []
      if (overrideOn) {
        items.push('<li>This sharer’s fee will be locked at the manual amount and the scheduled rate recalculation will skip this sharer until the override is removed.</li>')
      }
      if (overrideOff) {
        items.push('<li>This sharer’s fee will return to the calculated (automatic) rate and will recompute on its own — this may take a moment to appear.</li>')
      }
      if (rateChange) {
        if (clearingRate) {
          // XOL-514: clearing the rate is not a rate change, it is a STOP.
          // The generic bullet below ("calculated from the new rate's tiers")
          // describes a rate that no longer exists, so it must not fire here.
          //
          // Verified in the API rather than assumed, because this is billing
          // copy: RecalculateSiteSharerFees skips a licensee when
          // `is_null($rateId) && ! $licensee->rateLocked` — logging "no
          // rate_id — skipping, leaving currentRate untouched". So the fee
          // amount does NOT reset to zero; it freezes at whatever was last
          // computed, and no further fee rows are generated.
          //
          // The `! rateLocked` half of that condition is why this bullet is
          // gated on the override being OFF. An overridden licensee is
          // rateless-but-locked and keeps billing its frozen amount through
          // the locked ledger branch, so "stops being billed" would be false.
          // Today the rate picker only renders when the override is off
          // (template `v-if="!details.overrideEnabled"` on its row), but the
          // user can clear the rate and THEN tick Override in the same edit —
          // which is exactly the combination this guard covers. If the picker
          // is ever shown while overridden, re-check this bullet.
          if (this.details.overrideEnabled) {
            items.push('<li>This sharer’s rate will be removed. Its fee stays locked at the manual amount above, so billing continues.</li>')
          } else {
            items.push('<li><strong>This sharer will stop being billed.</strong> With no rate, no further fees are generated for it.</li>')
            items.push('<li>Its current fee amount is left as it is — it does not reset to zero — and past records are not affected.</li>')
            items.push('<li>Billing resumes when a rate is set again.</li>')
          }
        } else {
          items.push('<li>This sharer’s fees will be calculated from the new rate’s tiers going forward; past records are not affected.</li>')
        }
      }
      if (versionChange) {
        if (this.selectedVersionId) {
          items.push('<li>This sharer will be pinned to a specific rate version; future fees use that version’s frozen tiers and recompute in the background — this may take a moment.</li>')
        } else if (rateChange && !clearingRate) {
          // XOL-514: `!clearingRate` guard. Clearing the rate nulls
          // selectedVersionId (loadVersionsForRate's `!rateId` branch), which
          // makes versionChange true and lands here — promising a pin to "the
          // new rate's current published version" when there is no new rate
          // and commitFinance sends no version at all.
          //
          // XOL-497: was "will follow the current (live) rate again", true only
          // while commitFinance sent an explicit rate_version_id: null to
          // unpin. It now OMITS the key (API XOL-507 422s an explicit null),
          // and SharerController::update() auto-pins currentForRate() on a rate
          // change — so the sharer ends up PINNED, not following the live rate.
          // Reachable without `clearable`: selectedVersionId is null after a
          // failed versions GET, or when the new rate has versions but none
          // is_current.
          items.push('<li>This sharer will be pinned to the new rate’s current published version; its fee recomputes in the background.</li>')
        }
      }
      return '<p>Before saving, please note:</p><ul style="margin:0;padding-left:20px;text-align:left;">' + items.join('') + '</ul>'
    },

    // XOL-259: fold the re-pin response into local details so the fee display refreshes
    onVersionRepinned: function (licensee) {
      if (!licensee) return
      if (licensee.calculatedRate !== undefined) {
        this.details.calculatedRate = licensee.calculatedRate
      }
      if (licensee.currentRate !== undefined) {
        this.details.currentRate = licensee.currentRate
      }
      if (licensee.rateVersion !== undefined) {
        this.details.rateVersion = licensee.rateVersion
      }
      // XOL-373: was four inlined lines here; now the shared fold, so the
      // whole-object save, the re-pin and both override paths cannot drift
      // apart again.
      this.applyStamp(licensee)
    },

    editState: function () {
      if (this.editing) {
        this.details.started = this.formatDateSql(this.details.started)
        this.details.startBilling = this.formatDateSql(this.details.startBilling)
        this.details.expiry = this.formatDateSql(this.details.expiry)
        this.details.terminated = this.formatDateSql(this.details.terminated)

        // XOL-467: mirror LicenseeRequest::withValidator()'s own early
        // return — the API only checks the chain when the save actually
        // TOUCHES a date. Skipping validateDates() here when nothing dirty-
        // gated changed is what lets a pure rate/override edit save on the
        // ~72 Active licensees that already violate the chain (an Agreement
        // Start, no Handover) — the dirty-gate's whole reason for existing.
        //
        // Blocking, field-level, above BOTH exits below — the early
        // `commitFinance` a few lines down (when `needsConfirm` is false) and
        // the `.then(commitFinance)` after the fee-change confirm. A
        // date-only edit sets every fee flag false and leaves by the early
        // exit, so gating only the confirm path would let it bypass
        // validation entirely — the exact edit this exists to catch.
        if (this.chainFieldsDirty() && !this.validateDates()) return

        var overrideChanged = this.details.overrideEnabled !== this.originalOverride
        var rateChanged = Number(this.details.currentRate) !== Number(this.originalRate)
        // XOL-514: normalise both sides before comparing. Element's clearable
        // el-select emits '' rather than null, so a raw `'' !== null` reported
        // a RATE CHANGE for a licensee that has no rate and still has no rate —
        // and then showed it the "fees will be calculated from the new rate's
        // tiers" confirm, describing a change that is not happening.
        //
        // Not reachable from the UI today (Element hides the clear icon when
        // there is no value to clear), which is exactly why it would have sat
        // there: found by a test, not by clicking. `|| null` also folds
        // undefined, so a payload that omits the key compares equal to one
        // carrying an explicit null.
        var currentRateId = this.details.rate_id || null
        var rateIdChanged = currentRateId !== (this.originalRateId || null)
        var versionIdChanged = this.canSwitchVersion && (this.selectedVersionId !== this.originalVersionId)

        var overrideOn = this.details.overrideEnabled && (overrideChanged || rateChanged)
        var overrideOff = !this.details.overrideEnabled && overrideChanged
        var rateChange = rateIdChanged

        // XOL-514: a rate CLEAR, distinguished from a rate SWAP. Only true when
        // the licensee actually had a rate and now has none — an already-
        // rateless licensee echoing an empty picker is not clearing anything,
        // gets no dialog and no flag, and the API leaves it alone (738 such
        // rows on the current baseline).
        //
        // Truthiness, not `=== null`: Element's `clearable` el-select emits
        // '' on clear, not null.
        var clearingRate = !!this.originalRateId && !this.details.rate_id

        var needsConfirm = overrideOn || overrideOff || rateChange || versionIdChanged

        if (!needsConfirm) {
          this.commitFinance(overrideChanged, rateChanged, rateIdChanged, versionIdChanged, clearingRate)
          return
        }

        var self = this
        this.$confirm(
          this.buildImpactMessage(overrideOn, overrideOff, rateChange, versionIdChanged, clearingRate),
          // XOL-514: a clear stops billing, so it gets its own title and verb
          // rather than sharing "Confirm fee change" with a swap. The two are
          // not the same decision and must not read as the same decision.
          clearingRate && !this.details.overrideEnabled ? 'Stop billing this sharer?' : 'Confirm fee change',
          {
            confirmButtonText: clearingRate && !this.details.overrideEnabled ? 'Remove rate and stop billing' : 'Save changes',
            cancelButtonText: 'Keep editing',
            center: true,
            dangerouslyUseHTMLString: true,
            type: 'warning'
          }
        ).then(function () {
          self.commitFinance(overrideChanged, rateChanged, rateIdChanged, versionIdChanged, clearingRate)
        }).catch(function () {
          // cancel: write nothing, stay in edit mode
        })
      } else {
        this.editing = true
        this.resumingFee = false
        this.clearDateErrors()
        this.autoFilled = false
        // XOL-306: seed the version selector when entering edit mode
        if (this.details.rate_id) {
          this.loadVersionsForRate(this.details.rate_id, false)
        }
      }
    },

    // XOL-467: field -> message for `dateChainErrors()`. This tab has no
    // `handover` input — the chain's ROOT rule reroutes onto Agreement
    // Start, the field whose presence made the requirement true, naming
    // where to actually fix it. Site → Financials cannot repair its own
    // block; the dirty-gate is what removes the urgency (an unrelated rate
    // change still saves).
    dateChainCopy: function (item) {
      switch (item.code) {
        case 'handover_required':
          return {
            field: 'started',
            message: HANDOVER_REQUIRED_MESSAGE
          }
        case 'started_required':
          return {field: 'started', message: 'Required when Billing Start is set.'}
        case 'billing_start_required':
          return {field: 'startBilling', message: 'Required when Expiry is set.'}
        case 'billing_before_started':
          return {
            field: 'startBilling',
            message: 'Cannot be before Agreement Start (' + this.formatDate(this.details.started) + ').'
          }
        case 'expiry_before_billing_start':
          return {field: 'expiry', message: 'Must be after Billing Start.'}
        default:
          return null
      }
    },

    // XOL-467: true when this save would actually SEND one of the three date
    // keys commitFinance() dirty-gates (`handover` is never sent from this
    // tab at all). Mirrors LicenseeRequest::withValidator()'s own "touches no
    // date" early return.
    chainFieldsDirty: function () {
      return this.details.started !== this.originalStarted ||
        this.details.startBilling !== this.originalStartBilling ||
        this.details.expiry !== this.originalExpiry
    },

    // XOL-467: synchronous, hand-rolled — Element's own validate() is
    // unusable here (`v-model="details"`, not `:model`), and this form's
    // four date-inputs are keyless `v-if` siblings that get patch-reused on
    // a status flip (the XOL-451 always-passes trap Element's validate()
    // would fall into).
    validateDates: function () {
      var self = this
      var errors = dateChainErrors(this.details)
      var map = {}

      errors.forEach(function (item) {
        var copy = self.dateChainCopy(item)
        if (copy && !map[copy.field]) {
          map[copy.field] = copy.message
        }
      })

      this.fieldErrors = map

      var fields = Object.keys(map)
      if (fields.length > 0) {
        this.notifyBlockedSave(fields.length)
        this.focusDateField(fields[0])
        return false
      }

      return true
    },

    // Errors clear on ANY date @change — the whole map, since the rules are
    // cross-field. Also checks for Agreement Start <-> Billing Start
    // auto-fill (XOL-467) — `expiry` changes route through here too but are
    // never a member of DATE_CHAIN_FIELDS, so they no-op in maybeAutoFillDates.
    onDateChange: function (field, value) {
      this.clearDateErrors()
      if (!this.editing) return
      this.maybeAutoFillDates(field, value)
    },

    // XOL-467 (any-to-all widening): Agreement Start and Billing Start can
    // each source the other — this tab has no Handover input. Fires only
    // when the other field was empty before this edit — the same gate
    // doubles as the "do not fight the user" re-arm condition (see
    // autoFillDates()'s own doc in licenseeDates.js). Mutates `details`
    // directly, so a fill IS a genuine edit `chainFieldsDirty()` sees and
    // `commitFinance()` sends.
    maybeAutoFillDates: function (field, value) {
      var updates = autoFillDates(this.details, field, value, DATE_CHAIN_FIELDS)
      var keys = Object.keys(updates)
      if (keys.length === 0) return

      var self = this
      keys.forEach(function (key) { self.details[key] = updates[key] })
      this.autoFilled = true
    },

    // XOL-479: 422 backstop. validateDates() blocks everything the CLIENT can
    // see, but this tab's `details.handover` is a stale snapshot taken when
    // the component mounted — a concurrent edit on the Project tab (someone
    // else clears Handover between mount and Save) is invisible to it. The
    // API is the source of truth and re-checks the same chain
    // (LicenseeRequest::withValidator() in xolas-tms-api), returning the
    // error keyed on `handover` (its own root field — this tab reroutes onto
    // `started`, the same as the client-side path). PostMixins.send already
    // raises one $notify per Laravel validation error; this only adds the
    // SAME actionable affordance the client-side block shows, keyed off the
    // same message constant so `showHandoverAction` lights up either way.
    onSaveErrors: function (errors) {
      if (!errors || !errors.handover) return
      this.fieldErrors = Object.assign({}, this.fieldErrors, { started: HANDOVER_REQUIRED_MESSAGE })
      this.focusDateField('started')
    },

    // XOL-479: navigate to this licensee's Project tab (the only screen with
    // a Handover input) without losing the in-progress Financials edit.
    // `licensee` stacks as an L2 drawer ON TOP of the L1 `site` drawer this
    // component lives inside (LicenseeDetailLayer, drawerRegistry.js) — the
    // L1 route/component identity is unchanged by pushDrawer, so THIS
    // component is not unmounted and `details`/`editing` survive naturally.
    // Verified in src/components/atoms/layers/DrawerStack.vue: the layer-host
    // v-for is keyed on `entry.name + ':' + entry.args.id`, which does not
    // change when an L2 entry is appended.
    openHandoverOnProjectTab: function () {
      if (!this.sharer || !this.sharer.id || !this.$router) return
      pushDrawer(this.$router, { open: { licensee: { id: this.sharer.id, tab: 'project' } } })
    }
  },

  watch: {
    details: function (data) {
      this.details = data
    }
  },

  data: function () {
    var derived = this.deriveOverride(this.sharer)
    return {
      menu: 'agreement',
      editing: this.editMode,
      currency: settings.currency,
      details: Object.assign(this.clone(this.sharer), { overrideEnabled: derived }),
      originalRate: this.sharer.currentRate,
      originalOverride: derived,
      originalRateId: this.sharer.rate_id,
      // XOL-364: coerce to Number — the bulk-sharers payload serializes
      // rate_version_id as a string ("4"), but SelectRateVersions' options have
      // numeric ids and el-select matches by strict equality, so a string pin
      // matches no option and the selector renders the raw id instead of the label.
      originalVersionId: (this.sharer.rate_version_id != null && this.sharer.rate_version_id !== '') ? Number(this.sharer.rate_version_id) : null,
      // XOL-467: dirty-gate baselines for the date chain — see commitFinance().
      originalStarted: this.sharer.started,
      originalStartBilling: this.sharer.startBilling,
      originalExpiry: this.sharer.expiry,
      // XOL-467: any-to-all auto-fill notice flag — see maybeAutoFillDates().
      autoFilled: false,
      rateVersions: [],
      selectedVersionId: null,
      loadingVersions: false,
      resumingAuto: false,
      resumingFee: false,
      billingIssues: []
    }
  },

  mounted: function () {
    this.loadBillingIssues()
  }
}

</script>

<style scoped>
/* XOL-367: tighten the two-line "Override/Lock Rate" toggle label. Element's
   .el-form-item__label is line-height:40px, so two lines would be 40px taller
   than the single-line sibling labels — opening a large gap AND dropping the
   checkbox a row below the other inputs. line-height:20px makes the two lines
   occupy 40px (== one sibling line), so the gap tightens and the checkbox
   realigns with the Current Fees / Discount / Interest Rate inputs. */
.override-lock-label ::v-deep .el-form-item__label {
  line-height: 20px;
}

/* XOL-467: reserve room for the absolutely-positioned .el-form-item__error so
   a blocked-save message can never overlap the Sharer Rate row below.
   Applied via a parent-supplied class (not a bare ::v-deep .el-form-item rule
   — that leaks into every nested form, XOL-451) while editing, to every date
   field in the row so a message appearing/disappearing on one field never
   reflows the others. */
.licensee-date--editing {
  margin-bottom: 56px !important;
}

.licensee-date--editing ::v-deep .el-form-item__error {
  line-height: 1.4;
}

/* XOL-479: wraps the Agreement Start date-input so the "Open Project tab to
   set Handover" button can be positioned relative to it. Inline-block to
   match how el-form-item sits in this inline form (Element's own
   inline-form CSS), so wrapping it here does not change the row's flow. */
.handover-field {
  position: relative;
  display: inline-block;
  vertical-align: top;
}

/* The button sits BELOW the (up to 2-line) .el-form-item__error, inside the
   reserved gutter — .licensee-date--editing's 56px is tuned for the error
   text alone, so this field gets a taller reserve on top of it whenever the
   affordance is showing, extended enough that the button never overlaps the
   Sharer Rate row beneath (XOL-467's original overlap concern). */
.licensee-date--handover-gutter {
  margin-bottom: 94px !important;
}

.handover-field__action {
  position: absolute;
  left: 0;
  /* Centred in the band between the label text and the input's top border.
     Measured, not guessed: the label's glyph box ends 28px down the wrapper
     and the input starts at 50px, so the band is 22px for a 15px-tall button
     — only 7px of slack. 32px centres it: 4px clear of each.
       top:38  -> bottom 53, 3px INSIDE the input (sat on the border)
       top:28  -> flush against the label text, 0px above it
       top:32  -> 4px below the label, 4px above the input
     If the label's type size changes, re-measure the glyph box (a Range over
     the label's contents) rather than the padded element box — the element is
     50px tall and reading that is what produced the first two wrong values. */
  top: 32px;
  padding: 0;
}
</style>
